<template>
  <div class="arena-chips">
    <button
      v-if="canScrollPrev"
      type="button"
      class="arena-chips__next arena-chips__next--prev"
      :aria-label="$t('More arenas')"
      @click="scrollBy(-1)"
    >
      ‹
    </button>

    <div
      ref="track"
      class="arena-chips__track"
      :class="{ dragging: drag.moved }"
      @scroll="measure"
      @pointerdown="onDragStart"
      @click.capture="onTrackClick"
    >
      <button
        v-for="item in items"
        :key="item.key"
        type="button"
        class="arena-chips__chip"
        :class="[item.statusClass, { active: item.active }]"
        :disabled="item.disabled"
        :title="item.takenByOtherPane ? $t('Showing in the other screen') : null"
        @click="$emit('select', item)"
      >
        <img
          v-if="item.product === 'tai-xiu'"
          class="arena-chips__icon"
          :src="dieImage(item.index)"
          alt=""
        />
        <img v-else class="arena-chips__icon arena-chips__icon--flag" :src="flagImage(item.country)" alt="" />
        <span class="arena-chips__code">{{ item.code }}</span>
      </button>
    </div>

    <button
      v-if="canScrollNext"
      type="button"
      class="arena-chips__next"
      :aria-label="$t('More arenas')"
      @click="scrollBy(1)"
    >
      ›
    </button>
  </div>
</template>

<script>
/*
 * The arena rail of a desktop pane: every cockfight arena and every Tài Xỉu
 * table in one row, so either pane can show any of them.
 *
 * Items arrive ready-made from `desktop-client.vue` (which knows what the other
 * pane is showing and disables it); this component only draws them and emits
 * `select` with the item.
 *
 * Geometry is the Figma `tx-chips` node (457:6265) -- 30px pills, 18px glyph,
 * Montserrat ExtraBold 12px -- applied to both products so they read as one
 * rail. Cockfight chips keep the fight-status colours of the mobile arena rail
 * (`bet-panel/arena-status-indicator.vue`).
 *
 * The rail scrolls with the ‹ › buttons, the wheel/trackpad, or by clicking
 * and dragging it with the mouse (v1 let you drag its scrollbar; this one is
 * hidden). A drag that moved does not count as a click on the chip under it.
 */
const DRAG_THRESHOLD = 5;
const DIE_COUNT = 4;
const FALLBACK_FLAG =
  'data:image/webp;base64,UklGRlIAAABXRUJQVlA4IEYAAAAwAwCdASoUAA4APm0skkWkIqGYBABABsS0gAAt9Vmdj/IAAP77KW+67R0KYhmDrxRS2OF92nsnwKYkecnaCIIgrg84AAAA';

export default {
  name: 'ArenaChips',
  props: {
    // [{ key, product, id, code, index?, country?, statusClass?, active, disabled, takenByOtherPane }]
    items: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      canScrollPrev: false,
      canScrollNext: false,
      drag: { pointerId: null, startX: 0, startLeft: 0, ratio: 1, moved: false },
    };
  },
  watch: {
    items: {
      immediate: true,
      handler() {
        this.$nextTick(this.measure);
      },
    },
  },
  mounted() {
    this.measure();
    window.addEventListener('resize', this.measure);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.measure);
    this.stopDragListeners();
  },
  methods: {
    // The design draws a die per table, cycling through four faces; the table
    // list is server-driven and can be longer than that.
    dieImage(index) {
      return require(`@/assets/svg/tx-chips/die-${((index || 0) % DIE_COUNT) + 1}.svg`);
    },
    flagImage(country) {
      try {
        return require(`@/assets/images/flags/${country}.webp`);
      } catch (e) {
        return FALLBACK_FLAG;
      }
    },
    measure() {
      const track = this.$refs.track;
      if (!track) {
        this.canScrollPrev = false;
        this.canScrollNext = false;
        return;
      }
      this.canScrollPrev = track.scrollLeft > 1;
      this.canScrollNext = track.scrollLeft + track.clientWidth < track.scrollWidth - 1;
    },
    scrollBy(direction) {
      const track = this.$refs.track;
      if (!track) return;
      track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: 'smooth' });
    },
    onDragStart(event) {
      // Touch and pen already scroll natively; only the mouse needs help.
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      const track = this.$refs.track;
      if (!track || track.scrollWidth <= track.clientWidth) return;
      // The desktop frame is CSS-zoomed, so pointer pixels and scroll pixels
      // differ; this keeps the chips under the cursor while dragging.
      const rectWidth = track.getBoundingClientRect().width;
      this.drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startLeft: track.scrollLeft,
        ratio: rectWidth ? track.offsetWidth / rectWidth : 1,
        moved: false,
      };
      window.addEventListener('pointermove', this.onDragMove);
      window.addEventListener('pointerup', this.onDragEnd);
      window.addEventListener('pointercancel', this.onDragEnd);
    },
    onDragMove(event) {
      if (event.pointerId !== this.drag.pointerId) return;
      const dx = event.clientX - this.drag.startX;
      if (!this.drag.moved && Math.abs(dx) < DRAG_THRESHOLD) return;
      this.drag.moved = true;
      event.preventDefault();
      this.$refs.track.scrollLeft = this.drag.startLeft - dx * this.drag.ratio;
    },
    onDragEnd(event) {
      if (event.pointerId !== this.drag.pointerId) return;
      this.stopDragListeners();
      this.drag.pointerId = null;
      // Let the click that ends this drag be swallowed, then reset.
      setTimeout(() => {
        this.drag.moved = false;
      }, 0);
    },
    onTrackClick(event) {
      if (!this.drag.moved) return;
      event.stopPropagation();
      event.preventDefault();
    },
    stopDragListeners() {
      window.removeEventListener('pointermove', this.onDragMove);
      window.removeEventListener('pointerup', this.onDragEnd);
      window.removeEventListener('pointercancel', this.onDragEnd);
    },
  },
};
</script>

<style lang="scss" scoped>
// v2 chip look (status-coloured outline and code, dark fill) at the v2.1 28px height.
$chip-height: 28px;

.arena-chips {
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

.arena-chips__track {
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  scrollbar-width: none;
  cursor: grab;
  user-select: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &.dragging {
    cursor: grabbing;

    .arena-chips__chip {
      cursor: grabbing;
    }
  }

  img {
    -webkit-user-drag: none;
    pointer-events: none;
  }
}

.arena-chips__chip {
  --chip-color: #b2d6ea;
  --chip-text: #a1bbc9;

  flex: 0 0 auto;
  min-width: 56px;
  height: $chip-height;
  // The 1.5px outline is an inset shadow, not a border, so the glyph and the
  // label stay on whole pixels.
  padding: 0 10px;
  border: 0;
  box-shadow: inset 0 0 0 1.5px var(--chip-color);
  border-radius: 999px;
  background: rgba(18, 26, 44, 0.85);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  transition: background 0.15s;
  cursor: pointer;

  // Cockfight fight status, same palette as the mobile arena rail.
  &.green {
    --chip-color: #0eb14d;
    --chip-text: #0eb14d;
  }

  &.red {
    --chip-color: #e85050;
    --chip-text: #e85050;
  }

  &.blue {
    --chip-color: #2890fa;
    --chip-text: #2890fa;
  }

  &.orange {
    --chip-color: #ff8c38;
    --chip-text: #ff8c38;
  }

  &:disabled {
    opacity: 0.42;
    cursor: not-allowed;
  }

  /*
   * Active, as on the v1 arena rail (`arena-status-indicator.vue`): filled with
   * the status colour when there is one. With no status (betting not open) it
   * is not orange -- it keeps the dark fill with a white outline and label.
   */
  &.active {
    cursor: default;

    &:not(.green):not(.red):not(.blue):not(.orange) {
      --chip-color: #fff;
      --chip-text: #fff;
    }

    &.green,
    &.red,
    &.blue,
    &.orange {
      background: var(--chip-color);

      .arena-chips__code {
        color: #fff;
      }
    }
  }
}

.arena-chips__icon {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  display: block;
}

.arena-chips__icon--flag {
  border-radius: 50%;
  object-fit: cover;
}

.arena-chips__code {
  color: var(--chip-text);
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;
  text-transform: uppercase;
}

.arena-chips__next {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  padding: 0;
  box-sizing: border-box;
  border: 1px solid rgba(178, 214, 234, 0.45);
  border-radius: 50%;
  background: rgba(178, 214, 234, 0.08);
  color: #b2d6ea;
  font-size: 15px;
  line-height: 1;
  display: grid;
  place-items: center;
  cursor: pointer;
}
</style>
